
import { Component, Vue } from "vue-property-decorator";
import httpClient from "@/store/httpClient";
import rules from "@/store/rules";
import { CustomerInfoDto } from "@/dtos/customerInfoDto";
import { CustomerSettingsDto } from "@/dtos/customerSettingsDto";
import { supportedLocales, localeDisplayNames } from "@/plugins/i18n";

@Component({})
export default class CustomerSettings extends Vue {
	private customer: CustomerInfoDto = new CustomerInfoDto();
	private lastCustomer: CustomerInfoDto = new CustomerInfoDto();
	private canUserChangeSettings: boolean = false;
	private isValid: boolean = false;
	private isLoading: boolean = false;
	private useCompanyName: boolean = false;
	private languageOptions = supportedLocales.map((value) => ({
		value,
		text: localeDisplayNames[value],
	}));
	private requiredStringMax35Rule = rules.requiredStringMax35Rule;
	private requiredEmailRule = rules.requiredEmailRule;
	private optionalEmailRule = rules.optionalEmailRule;
	private addressRequiredStringMax35Rule = rules.addressRequiredStringMax35Rule;
	private companyNameRule = [
		(v: string) => !!v || (this.$t("settingsPages.customerSettings.requiredMessage") as string),
		(v: string) =>
			v.replace(/\s/g, "").length > 0 ||
			(this.$t("settingsPages.customerSettings.whitespaceOnlyMessage") as string),
		(v: string) =>
			!v || v.length <= 50 || (this.$t("settingsPages.customerSettings.maxLength50Message") as string),
		(v: string) =>
			!this.useCompanyName ||
			v !== "privat" ||
			(this.$t("settingsPages.customerSettings.companyNamePrivateMessage") as string),
	];
	private correctPlzRule = [
		(v: string) => {
			const isValid =
				(this.getCustomerCountry() !== "Deutschland" &&
					this.getCustomerCountry() !== "Schweiz" &&
					this.getCustomerCountry() !== "Liechtenstein") ||
				(this.getCustomerCountry() === "Deutschland" &&
					v.length === 5 &&
					parseInt(v) >= 1000 &&
					parseInt(v) <= 99999) ||
				((this.getCustomerCountry() === "Schweiz" ||
					this.getCustomerCountry() === "Liechtenstein") &&
					v.length === 4 &&
					parseInt(v) >= 1000 &&
					parseInt(v) <= 9999);

			return isValid || (this.$t("settingsPages.customerSettings.invalidZipMessage") as string);
		},
	];

	private customerMwstNumberRule = [
		(v: string) =>
			v.length <= 50 || (this.$t("settingsPages.customerSettings.maxLength50Message") as string),
	];

	private mounted() {
		this.loadCustomer();
		this.setRights();
	}

	private get hasChanges() {
		return (
			this.lastCustomer.companyName !== this.customer.companyName ||
			this.lastCustomer.street !== this.customer.street ||
			this.lastCustomer.town !== this.customer.town ||
			this.lastCustomer.plz !== this.customer.plz ||
			this.lastCustomer.generalPhone !== this.customer.generalPhone ||
			this.lastCustomer.generalEmail !== this.customer.generalEmail ||
			this.lastCustomer.customerMwstNumber !== this.customer.customerMwstNumber ||
			this.lastCustomer.defaultAdditionalOrderConfirmationEmail !==
				this.customer.defaultAdditionalOrderConfirmationEmail ||
			this.lastCustomer.correspondenceLanguage !== this.customer.correspondenceLanguage
		);
	}

	private loadCustomer() {
		this.customer = JSON.parse(JSON.stringify(this.$store.state.customer));
		if (!this.customer.correspondenceLanguage) {
			this.customer.correspondenceLanguage = "de-CH";
		}
		this.useCompanyName = this.customer.companyName !== "privat";
		this.lastCustomer = JSON.parse(JSON.stringify(this.customer));
	}

	private async setRights() {
		const response = await httpClient().get("Customer/CanUserManageUsers");
		this.canUserChangeSettings = response.data;
	}

	private async saveCustomer() {
		if (
			this.isValid &&
			!this.isLoading &&
			this.hasChanges &&
			!(this.useCompanyName && this.customer.companyName === "")
		) {
			try {
				this.isLoading = true;
				const settings: CustomerSettingsDto = {
					customerId: this.customer.customerId,
					companyName: this.customer.companyName,
					street: this.customer.street,
					plz: this.customer.plz,
					town: this.customer.town,
					generalEmail: this.customer.generalEmail,
					generalPhone: this.customer.generalPhone,
					customerMwstNumber: this.customer.customerMwstNumber,
					defaultAdditionalOrderConfirmationEmail:
						this.customer.defaultAdditionalOrderConfirmationEmail,
					correspondenceLanguage: this.customer.correspondenceLanguage,
				};
				await httpClient().post("Customer/SaveCustomerSettings", settings);
				this.$store.commit("updateCustomerSettings", {
					companyName: this.customer.companyName,
					phone: this.customer.generalPhone,
					email: this.customer.generalEmail,
					customerMwstNumber: this.customer.customerMwstNumber,
					defaultAdditionalOrderConfirmationEmail:
						this.customer.defaultAdditionalOrderConfirmationEmail,
					correspondenceLanguage: this.customer.correspondenceLanguage,
				});
				this.$store.dispatch(
					"setSnackbarText",
					this.$t("settingsPages.customerSettings.messages.saveSuccess")
				);
				this.lastCustomer = JSON.parse(JSON.stringify(this.customer));
			} catch (err) {
				this.$store.dispatch(
					"setSnackbarErrorText",
					this.$t("settingsPages.customerSettings.messages.saveError")
				);
			} finally {
				this.isLoading = false;
			}
		}
	}

	private getCustomerCountry() {
		return this.customer ? (this.customer.country ? this.customer.country : "") : "";
	}

	private validate() {
		(this.$refs.form as any).validate();
	}
}
